import { supabase } from "@/integrations/supabase/client";
import { Agent } from "@/types/agent/agent";
import {
  MarketplaceCategory,
  MarketplaceFilters,
  MarketplaceGenie,
  MarketplaceGenieDetail,
  MarketplaceGenieWithDetails,
  MarketplaceStats,
} from "@/types/marketplace";

// TODO refactor to use api

export class MarketplaceService {
  // ============ Categories ============

  static async listCategories(): Promise<MarketplaceCategory[]> {
    try {
      console.log("[MarketplaceService] Fetching categories from marketplace_categories table...");
      console.trace("[MarketplaceService] listCategories called");

      const { data, error } = await supabase
        .from("marketplace_categories")
        .select("*")
        .order("order", { ascending: true });

      if (error) {
        console.error("[MarketplaceService] Error fetching categories:", error);
        throw error;
      }
      console.log("[MarketplaceService] Categories fetched:", data?.length || 0, "categories", data);
      return data || [];
    } catch (error) {
      console.error("[MarketplaceService] Exception fetching marketplace categories:", error);
      return [];
    }
  }

  static async getCategoryBySlug(slug: string): Promise<MarketplaceCategory | null> {
    try {
      const { data, error } = await supabase.from("marketplace_categories").select("*").eq("slug", slug).single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching category:", error);
      return null;
    }
  }

  static async getCategoryById(id: string): Promise<MarketplaceCategory | null> {
    try {
      const { data, error } = await supabase.from("marketplace_categories").select("*").eq("id", id).single();

      if (error) {
        if (error.code === "PGRST116") return null;
        throw error;
      }
      return data;
    } catch (error) {
      console.error("Error fetching category by id:", error);
      return null;
    }
  }

  static async listCategoriesRecent(limit: number = 20): Promise<MarketplaceCategory[]> {
    try {
      const { data, error } = await supabase
        .from("marketplace_categories")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("[MarketplaceService] Error listing recent categories:", error);
      return [];
    }
  }

  static async searchCategories(query: string, limit: number = 20): Promise<MarketplaceCategory[]> {
    try {
      if (!query.trim()) {
        return this.listCategoriesRecent(limit);
      }
      const { data, error } = await supabase
        .from("marketplace_categories")
        .select("*")
        .or(`name.ilike.%${query}%,slug.ilike.%${query}%`)
        .order("name", { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("[MarketplaceService] Error searching categories:", error);
      return [];
    }
  }

  // ============ Browse & Search ============

  static async listMarketplaceGenies(filters?: MarketplaceFilters): Promise<MarketplaceGenie[]> {
    try {
      console.log("[MarketplaceService] Fetching genies with filters:", filters);
      let query = supabase.from("marketplace_genies").select("*").eq("is_public", true);

      // Apply filters
      if (filters?.category) {
        const category = await this.getCategoryBySlug(filters.category);
        if (category) {
          query = query.eq("category_id", category.id);
        }
      }

      // @ts-ignore - brand filter added for brand/product filtering
      if (filters?.brand) {
        // @ts-ignore
        const { data: brand } = await supabase
          .from("marketplace_brands")
          .select("id")
          // @ts-ignore
          .eq("slug", filters.brand)
          .single();
        if (brand) {
          query = query.eq("brand_id", brand.id);
        }
      }

      // @ts-ignore - product filter added for brand/product filtering
      if (filters?.product) {
        // @ts-ignore
        const { data: product } = await supabase
          .from("marketplace_products")
          .select("id")
          // @ts-ignore
          .eq("slug", filters.product)
          .single();
        if (product) {
          query = query.eq("product_id", product.id);
        }
      }

      if (filters?.difficulty) {
        query = query.contains("metadata", { difficulty: filters.difficulty });
      }

      if (filters?.industry) {
        query = query.contains("metadata", { industry: filters.industry });
      }

      if (filters?.isFeatured) {
        query = query.eq("is_featured", true);
      }

      if (filters?.minRating) {
        query = query.gte("rating", filters.minRating);
      }

      if (filters?.search) {
        query = query.or(
          `display_name.ilike.%${filters.search}%,tagline.ilike.%${filters.search}%,description.ilike.%${filters.search}%`,
        );
      }

      // Apply sorting
      switch (filters?.sortBy) {
        case "popular":
          query = query.order("clone_count", { ascending: false });
          break;
        case "newest":
          query = query.order("created_at", { ascending: false });
          break;
        case "rating":
          query = query.order("rating", { ascending: false });
          break;
        case "clones":
          query = query.order("clone_count", { ascending: false });
          break;
        default:
          query = query.order("is_featured", { ascending: false }).order("clone_count", { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error("[MarketplaceService] Error listing genies:", error);
        throw error;
      }
      console.log("[MarketplaceService] Genies fetched:", data?.length || 0, "genies");
      return data || [];
    } catch (error) {
      console.error("[MarketplaceService] Exception listing marketplace genies:", error);
      return [];
    }
  }

  /**
   * List marketplace genies with pagination support
   */
  static async listMarketplaceGeniesWithPagination(filters?: MarketplaceFilters & { page?: number; limit?: number }) {
    try {
      const page = filters?.page || 1;
      const limit = filters?.limit || 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase.from("marketplace_genies").select("*", { count: "exact" }).eq("is_public", true);

      // Apply filters (same as above)
      if (filters?.category) {
        const category = await this.getCategoryBySlug(filters.category);
        if (category) {
          query = query.eq("category_id", category.id);
        }
      }

      if (filters?.difficulty) {
        query = query.contains("metadata", { difficulty: filters.difficulty });
      }

      if (filters?.industry) {
        query = query.contains("metadata", { industry: filters.industry });
      }

      if (filters?.isFeatured) {
        query = query.eq("is_featured", true);
      }

      if (filters?.minRating) {
        query = query.gte("rating", filters.minRating);
      }

      // Use full-text search if available
      if (filters?.search) {
        query = query.textSearch("search_vector", filters.search, {
          type: "websearch",
          config: "english",
        });
      }

      // Apply sorting
      switch (filters?.sortBy) {
        case "popular":
          query = query.order("clone_count", { ascending: false });
          break;
        case "newest":
          query = query.order("created_at", { ascending: false });
          break;
        case "rating":
          query = query.order("rating", { ascending: false });
          break;
        case "clones":
          query = query.order("clone_count", { ascending: false });
          break;
        default:
          query = query.order("is_featured", { ascending: false }).order("clone_count", { ascending: false });
      }

      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      const totalPages = count ? Math.ceil(count / limit) : 0;

      return {
        data: data || [],
        pagination: {
          page,
          limit,
          total: count || 0,
          totalPages,
          hasMore: page < totalPages,
        },
      };
    } catch (error) {
      console.error("Error listing marketplace genies with pagination:", error);
      return {
        data: [],
        pagination: {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasMore: false,
        },
      };
    }
  }

  /**
   * Full-text search using PostgreSQL tsvector
   */
  static async fullTextSearch(searchQuery: string, options?: { limit?: number; offset?: number }) {
    try {
      const limit = options?.limit || 20;
      const offset = options?.offset || 0;

      const { data, error } = await supabase
        .from("marketplace_genies")
        .select("*")
        .eq("is_public", true)
        .textSearch("search_vector", searchQuery, {
          type: "websearch",
          config: "english",
        })
        .order("clone_count", { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error performing full-text search:", error);
      // Fallback to basic search
      return this.searchMarketplaceGenies(searchQuery);
    }
  }

  static async searchMarketplaceGenies(query: string): Promise<MarketplaceGenie[]> {
    return this.listMarketplaceGenies({ search: query });
  }

  static async getGeniesByCategory(categorySlug: string): Promise<MarketplaceGenie[]> {
    return this.listMarketplaceGenies({ category: categorySlug });
  }

  static async getFeaturedGenies(limit: number = 6): Promise<MarketplaceGenie[]> {
    try {
      const { data, error } = await supabase
        .from("marketplace_genies")
        .select("*")
        .eq("is_public", true)
        .eq("is_featured", true)
        .order("clone_count", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching featured genies:", error);
      return [];
    }
  }

  // ============ Detail ============

  static async getMarketplaceGenie(slug: string): Promise<MarketplaceGenieDetail | null> {
    try {
      // Get genie with details
      const { data: genieData, error: genieError } = await supabase
        .from("marketplace_genies_with_agents")
        .select("*")
        .eq("slug", slug)
        .eq("is_public", true)
        .single();

      if (genieError) throw genieError;
      if (!genieData) return null;

      // Get full agent details
      //const agent = await AgentService.getAgent(genieData.agent_id);
      //if (!agent) return null;
      const agent = null;

      // Get related genies
      const relatedGenies = await this.getRelatedGenies(genieData.category_id, genieData.id, 3);

      return {
        genie: genieData as MarketplaceGenieWithDetails,
        agent: agent as Agent,
        related_genies: relatedGenies,
      };
    } catch (error) {
      console.error("Error fetching marketplace genie:", error);
      return null;
    }
  }

  static async getMarketplaceGenieById(id: string): Promise<MarketplaceGenie | null> {
    try {
      const { data, error } = await supabase.from("marketplace_genies").select("*").eq("id", id).single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching marketplace genie by ID:", error);
      return null;
    }
  }

  static async getRelatedGenies(
    categoryId: string | null,
    currentGenieId: string,
    limit: number = 3,
  ): Promise<MarketplaceGenie[]> {
    try {
      let query = supabase
        .from("marketplace_genies")
        .select("*")
        .eq("is_public", true)
        .neq("id", currentGenieId)
        .limit(limit);

      if (categoryId) {
        query = query.eq("category_id", categoryId);
      }

      query = query.order("clone_count", { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching related genies:", error);
      return [];
    }
  }

  // ============ Brands & Products ============

  static async listBrands(filters?: { category_id?: string; search?: string }) {
    try {
      console.log("[MarketplaceService] Fetching brands with filters:", filters);
      let query = supabase.from("marketplace_brands").select("*").order("order_index", { ascending: true });

      if (filters?.category_id) {
        query = query.eq("category_id", filters.category_id);
      }

      if (filters?.search) {
        query = query.ilike("name", `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("[MarketplaceService] Error fetching brands:", error);
        throw error;
      }
      console.log("[MarketplaceService] Brands fetched:", data?.length || 0, "brands");
      return data || [];
    } catch (error) {
      console.error("[MarketplaceService] Exception fetching brands:", error);
      return [];
    }
  }

  static async listProducts(filters?: { brand_id?: string; category_id?: string; search?: string }) {
    try {
      console.log("[MarketplaceService] Fetching products with filters:", filters);
      let query = supabase.from("marketplace_products").select("*").order("order_index", { ascending: true });

      if (filters?.brand_id) {
        query = query.eq("brand_id", filters.brand_id);
      }

      if (filters?.category_id) {
        query = query.eq("category_id", filters.category_id);
      }

      if (filters?.search) {
        query = query.ilike("name", `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("[MarketplaceService] Error fetching products:", error);
        throw error;
      }
      console.log("[MarketplaceService] Products fetched:", data?.length || 0, "products");
      return data || [];
    } catch (error) {
      console.error("[MarketplaceService] Exception fetching products:", error);
      return [];
    }
  }

  static async getProductBySlug(slug: string) {
    try {
      const { data, error } = await supabase.from("marketplace_products").select("*").eq("slug", slug).single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching product by slug:", error);
      return null;
    }
  }

  static async getBrandBySlug(slug: string) {
    try {
      const { data, error } = await supabase.from("marketplace_brands").select("*").eq("slug", slug).single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching brand by slug:", error);
      return null;
    }
  }

  // ============ Analytics ============

  static async incrementViewCount(genieId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc("increment", {
        row_id: genieId,
        table_name: "marketplace_genies",
        column_name: "view_count",
      });

      if (error) {
        // Fallback to manual increment if RPC doesn't exist
        const genie = await this.getMarketplaceGenieById(genieId);
        if (genie) {
          await supabase
            .from("marketplace_genies")
            .update({ view_count: (genie.view_count || 0) + 1 })
            .eq("id", genieId);
        }
      }
    } catch (error) {
      console.error("Error incrementing view count:", error);
    }
  }

  static async incrementCloneCount(genieId: string): Promise<void> {
    try {
      const genie = await this.getMarketplaceGenieById(genieId);
      if (genie) {
        await supabase
          .from("marketplace_genies")
          .update({ clone_count: (genie.clone_count || 0) + 1 })
          .eq("id", genieId);
      }
    } catch (error) {
      console.error("Error incrementing clone count:", error);
    }
  }

  static async getMarketplaceStats(): Promise<MarketplaceStats> {
    try {
      const [geniesResult, categoriesResult] = await Promise.all([
        supabase.from("marketplace_genies").select("id, view_count, clone_count, is_featured").eq("is_public", true),
        supabase.from("marketplace_categories").select("id"),
      ]);

      const genies = geniesResult.data || [];
      const categories = categoriesResult.data || [];

      return {
        total_genies: genies.length,
        total_clones: genies.reduce((sum, g) => sum + (g.clone_count || 0), 0),
        total_views: genies.reduce((sum, g) => sum + (g.view_count || 0), 0),
        featured_count: genies.filter((g) => g.is_featured).length,
        categories_count: categories.length,
      };
    } catch (error) {
      console.error("Error fetching marketplace stats:", error);
      return {
        total_genies: 0,
        total_clones: 0,
        total_views: 0,
        featured_count: 0,
        categories_count: 0,
      };
    }
  }
}
